import { ExternalLink, Lock, LockOpen } from "lucide-react";
import { AccessibleSheet } from "@/components/common/AccessibleSheet";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { getTypeLabel, getDisplayName, formatAgentId } from "../utils";

interface IntegrationDetailsSheetProps {
  sourceId: number | null;
  sources: any[];
  onClose: () => void;
  onToggleActive: (data: { sourceId: number; isActive: boolean }) => void;
}

const IntegrationDetailsSheet = ({
  sourceId,
  sources,
  onClose,
  onToggleActive,
}: IntegrationDetailsSheetProps) => {
  if (!sourceId) return null;

  const source = sources.find((s) => s.id === sourceId);
  if (!source) return null;

  return (
    <AccessibleSheet
      isOpen={!!sourceId}
      onClose={onClose}
      title="Integration Details"
      className="w-full sm:max-w-xl"
    >
      <div className="flex items-start sm:items-center justify-between border-b p-4 sm:p-6 gap-4">
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <Badge variant="outline" className="text-xs flex-shrink-0 border-gray-300 text-gray-600">
            {getTypeLabel(source.source_type)}
          </Badge>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 break-words truncate">
            {getDisplayName(source)}
          </h2>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-2xl font-light flex-shrink-0"
        >
          &times;
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-6 py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Status</h3>
            <p className="text-sm text-gray-600">{source.is_active ? "Active" : "Inactive"}</p>
          </div>
          <Switch
            checked={source.is_active}
            onCheckedChange={(isActive) =>
              onToggleActive({ sourceId: source.id, isActive })
            }
          />
        </div>

        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Credentials</h3>
          <div className="flex items-center gap-1" title={source.credential_id ? "Credentials attached" : "No credentials"}>
            <span className="text-sm text-gray-600">
              {source.credential_id ? "Attached" : "Not attached"}
            </span>
            {source.credential_id ? (
              <Lock className="h-4 w-4 text-gray-400" />
            ) : (
              <LockOpen className="h-4 w-4 text-gray-300" />
            )}
          </div>
        </div>

        {source.last_capabilities_fetch && (
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Last Updated</h3>
            <p className="text-sm text-gray-600">
              {new Date(source.last_capabilities_fetch).toLocaleString()}
            </p>
          </div>
        )}

        {source.agents && source.agents.length > 0 && (
          <div className="border-t border-gray-200 py-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Connected Agents ({source.agents.length})</h3>
            <ul className="space-y-2">
              {source.agents.map((agent: any, idx: number) => (
                <li key={idx}>
                  <a
                    href={`/genie-hub/my-genies/${agent.agent_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 text-sm text-gray-900 hover:bg-gray-50 rounded-lg transition-colors group"
                  >
                    <span>{formatAgentId(agent.agent_id || "")}</span>
                    <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="border-t border-b border-gray-200 py-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Capabilities</h3>
          {source.capabilities?.resources && source.capabilities.resources.length > 0 ? (
            <ul className="space-y-1 mb-3 min-w-0">
              {source.capabilities.resources.map((res: any, idx: number) => (
                <li key={idx} className="text-sm text-gray-600 break-words [overflow-wrap:anywhere]">
                  • {res.name}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 italic mb-3">No capabilities attached</p>
          )}
          {source.last_capabilities_fetch && (
            <div className="flex items-center justify-between pt-3">
              <h3 className="text-sm font-semibold text-gray-900">Capabilities Refreshed At</h3>
              <p className="text-sm text-gray-600">
                {new Date(source.last_capabilities_fetch).toLocaleString()}
              </p>
            </div>
          )}
        </div>

        {source.config && Object.keys(source.config).length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Configuration</h3>
            <div className="space-y-2">
              {Object.entries(source.config).map(([key, value]) => (
                <div
                  key={key}
                  className="grid min-w-0 gap-3 py-2 border-b border-gray-200 last:border-0"
                  style={{ gridTemplateColumns: "120px minmax(0, 1fr)" }}
                >
                  <p className="text-sm font-medium text-gray-600 break-words">
                    {key}
                  </p>
                  <p className="min-w-0 text-sm text-gray-900 break-words [overflow-wrap:anywhere]">
                    {typeof value === "string" ? value : JSON.stringify(value)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AccessibleSheet>
  );
};

export default IntegrationDetailsSheet;
